import * as THREE from "three";

/**
 * A class to create and manage a gold sparkle particle effect
 */
export class ParticleSystem {
    /**
     * Create a new gold sparkle particle system
     * @param {THREE.Scene} scene - The scene to add the particles to
     * @param {Object} options - Configuration options for the particle system
     */
    constructor(scene, options = {}) {
        this.scene = scene;

        // Default options with gold sparkle settings
        this.options = {
            count: options.count || 500,
            size: options.size || { min: 0.3, max: 1.2 },
            area: options.area || {
                width: 200,
                height: 150,
                depth: 200,
            },
            color: options.color || 0xffd700, // Gold color
            secondaryColor: options.secondaryColor || 0xfffacd, // Light yellow
            speed: options.speed || { min: 0.2, max: 1.5 },
            lifetime: options.lifetime || { min: 2, max: 6 },
            texture: options.texture || null,
            blending: options.blending || THREE.AdditiveBlending,
            opacity: options.opacity || { min: 0.4, max: 0.8 },
        };

        this.particles = null;
        this.particleSystem = null;
        this.clock = new THREE.Clock();
        this.particlesData = [];

        this.initialize();
    }

    /**
     * Initialize the particle system
     */
    initialize() {
        // Create geometry for particles
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(this.options.count * 3);
        const colors = new Float32Array(this.options.count * 3);
        const sizes = new Float32Array(this.options.count);
        const opacities = new Float32Array(this.options.count);

        // Setup particle attributes
        for (let i = 0; i < this.options.count; i++) {
            // Random position within area
            const x = (Math.random() - 0.5) * this.options.area.width;
            const y = (Math.random() - 0.5) * this.options.area.height + 165; // Center around model's height
            // Position particles further behind the platform (more negative z values)
            const z = (Math.random() - 0.5) * this.options.area.depth - 300; // Increased offset to place further behind the model

            positions[i * 3] = x;
            positions[i * 3 + 1] = y;
            positions[i * 3 + 2] = z;

            // Randomize between multiple colors (black, gold, white, and blue)
            let color;
            const colorRandom = Math.random();
            if (colorRandom < 0.25) {
                // Black
                color = new THREE.Color(0x000000);
            } else if (colorRandom < 0.5) {
                // Gold
                color = new THREE.Color(0xffd700);
            } else if (colorRandom < 0.75) {
                // White
                color = new THREE.Color(0xffffff);
            } else {
                // Blue (original color)
                color = new THREE.Color(this.options.color);
            }

            colors[i * 3] = color.r;
            colors[i * 3 + 1] = color.g;
            colors[i * 3 + 2] = color.b;

            // Random size
            sizes[i] =
                Math.random() *
                    (this.options.size.max - this.options.size.min) +
                this.options.size.min;

            // Initial opacity
            opacities[i] =
                Math.random() *
                    (this.options.opacity.max - this.options.opacity.min) +
                this.options.opacity.min;

            // Store additional data for animation
            this.particlesData.push({
                velocity: new THREE.Vector3(
                    (Math.random() - 0.5) * this.options.speed.max,
                    (Math.random() - 0.5) * this.options.speed.min,
                    (Math.random() - 0.5) * this.options.speed.max,
                ),
                lifetime:
                    Math.random() *
                        (this.options.lifetime.max -
                            this.options.lifetime.min) +
                    this.options.lifetime.min,
                age: 0,
                size: sizes[i],
                opacity: opacities[i],
            });
        }

        // Add attributes to geometry
        geometry.setAttribute(
            "position",
            new THREE.BufferAttribute(positions, 3),
        );
        geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
        geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
        geometry.setAttribute(
            "opacity",
            new THREE.BufferAttribute(opacities, 1),
        );

        // Create particle material
        let material;

        if (this.options.texture) {
            // If a texture is provided, use it
            const texture = new THREE.TextureLoader().load(
                this.options.texture,
            );
            material = new THREE.PointsMaterial({
                size: 1,
                map: texture,
                blending: this.options.blending,
                depthWrite: false,
                transparent: true,
                vertexColors: true,
            });
        } else {
            // Otherwise create a custom sparkle texture
            const canvas = document.createElement("canvas");
            canvas.width = 32;
            canvas.height = 32;
            const ctx = canvas.getContext("2d");

            // Draw a soft, glowing circle
            const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
            gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
            gradient.addColorStop(0.3, "rgba(255, 255, 240, 0.8)");
            gradient.addColorStop(1, "rgba(255, 255, 215, 0)");

            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 32, 32);

            // Create a Three.js texture from the canvas
            const texture = new THREE.CanvasTexture(canvas);
            material = new THREE.PointsMaterial({
                size: 1,
                map: texture,
                blending: this.options.blending,
                depthWrite: false,
                transparent: true,
                vertexColors: true,
                alphaMap: texture,
            });
        }

        // Create the particle system
        this.particleSystem = new THREE.Points(geometry, material);
        this.particleSystem.frustumCulled = false; // Prevent particles from disappearing
        this.scene.add(this.particleSystem);
    }

    /**
     * Update the particle system animation
     */
    update() {
        if (!this.particleSystem) return;

        const delta = this.clock.getDelta();

        const positions =
            this.particleSystem.geometry.attributes.position.array;
        const sizes = this.particleSystem.geometry.attributes.size.array;
        const opacities = this.particleSystem.geometry.attributes.opacity.array;

        for (let i = 0; i < this.options.count; i++) {
            const data = this.particlesData[i];

            // Update particle position based on velocity
            positions[i * 3] += data.velocity.x * delta;
            positions[i * 3 + 1] += data.velocity.y * delta;
            positions[i * 3 + 2] += data.velocity.z * delta;

            // Update particle age
            data.age += delta;

            // Calculate lifetime progress (0 to 1)
            const progress = data.age / data.lifetime;

            // Update opacity based on lifetime (fade in and out)
            if (progress < 0.2) {
                // Fade in
                opacities[i] = data.opacity * (progress / 0.2);
            } else if (progress > 0.8) {
                // Fade out
                opacities[i] = data.opacity * (1 - (progress - 0.8) / 0.2);
            } else {
                // Full opacity during middle of lifetime
                opacities[i] = data.opacity;
            }

            // Size pulsation
            sizes[i] = data.size * (1 + 0.2 * Math.sin(progress * Math.PI * 4));

            // Reset particle if its lifetime is over
            if (data.age >= data.lifetime) {
                // Reset position
                positions[i * 3] =
                    (Math.random() - 0.5) * this.options.area.width;
                positions[i * 3 + 1] =
                    (Math.random() - 0.5) * this.options.area.height + 165;
                positions[i * 3 + 2] =
                    (Math.random() - 0.5) * this.options.area.depth - 300; // Place further behind the model

                // Reset color to a random selection (black, gold, white, or blue)
                const colors =
                    this.particleSystem.geometry.attributes.color.array;
                let newColor;
                const colorRandom = Math.random();
                if (colorRandom < 0.25) {
                    // Black
                    newColor = new THREE.Color(0x000000);
                } else if (colorRandom < 0.5) {
                    // Gold
                    newColor = new THREE.Color(0xffd700);
                } else if (colorRandom < 0.75) {
                    // White
                    newColor = new THREE.Color(0xffffff);
                } else {
                    // Blue (original color)
                    newColor = new THREE.Color(this.options.color);
                }
                colors[i * 3] = newColor.r;
                colors[i * 3 + 1] = newColor.g;
                colors[i * 3 + 2] = newColor.b;

                // Reset velocity
                data.velocity.set(
                    (Math.random() - 0.5) * this.options.speed.max,
                    (Math.random() - 0.5) * this.options.speed.min,
                    (Math.random() - 0.5) * this.options.speed.max,
                );

                // Reset age and lifetime
                data.age = 0;
                data.lifetime =
                    Math.random() *
                        (this.options.lifetime.max -
                            this.options.lifetime.min) +
                    this.options.lifetime.min;
            }
        }

        // Update the geometry attributes
        this.particleSystem.geometry.attributes.position.needsUpdate = true;
        this.particleSystem.geometry.attributes.size.needsUpdate = true;
        this.particleSystem.geometry.attributes.opacity.needsUpdate = true;
        this.particleSystem.geometry.attributes.color.needsUpdate = true; // Make sure colors are updated as well
    }

    /**
     * Dispose of all resources used by the particle system
     */
    dispose() {
        if (this.particleSystem) {
            this.scene.remove(this.particleSystem);
            this.particleSystem.geometry.dispose();
            this.particleSystem.material.dispose();
            if (this.particleSystem.material.map) {
                this.particleSystem.material.map.dispose();
            }
        }
        this.particlesData = [];
    }
}
